import { ReactElement } from 'react';

import { IconProps } from '@components/icons';
import { IconAskAI } from '@components/icons/ask-ai';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconOutput } from '@components/icons/output';
import { IconTextLong } from '@components/icons/text-long';
import { InfoBox } from '@components/info-box';
import { VariableValue } from '@components/text-editors/addons/field-mapping';
import { getScopedTranslate, useScopedTranslate } from '@locale/translate';

import { CompanyUserOptions } from 'api/authentication/requests';

import { InfoBoxClosable, InfoBoxVariant } from '../common';

export type TemplateName =
  | 'moodCheck'
  | 'ratingCheck'
  | 'summarizeText'
  | 'validateDriversLicense'
  | 'validatePassport'
  | 'validateID'
  | 'validateEducationalRecord'
  | 'validateCriminalRecord'
  | 'other'
  | 'cvRanking'
  | 'cvSummarize';

export const getPromptValue = (templateName: TemplateName): VariableValue => {
  const { t } = getScopedTranslate(locale);
  return { value: t(templateName) };
};

export const getPromptTemplateOptions = () => {
  const { t } = getScopedTranslate(locale);
  return [
    {
      value: 'moodCheck',
      label: t('moodCheckTitle'),
    },
    {
      value: 'ratingCheck',
      label: t('ratingCheckTitle'),
    },
    {
      value: 'validateDriversLicense',
      label: t('validateDriversLicenseTitle'),
    },
    {
      value: 'validatePassport',
      label: t('validatePassportTitle'),
    },
    {
      value: 'validateID',
      label: t('validateIDTitle'),
    },
    {
      value: 'validateEducationalRecord',
      label: t('validateEducationalRecordTitle'),
    },
    {
      value: 'validateCriminalRecord',
      label: t('validateCriminalRecordTitle'),
    },
    {
      value: 'other',
      label: t('otherTitle'),
    },
    {
      value: 'cvRanking',
      label: t('cvRankingTitle'),
    },
    {
      value: 'cvSummarize',
      label: t('cvSummarizeTitle'),
    },
    {
      value: 'summarizeText',
      label: t('summarizeTextTitle'),
    },
  ];
};

const locale = {
  en: {
    moodCheckTitle: 'Mood check',
    moodCheck:
      'Please review the text given and categorize based on the general mood of the text and the author at the time of creation.',
    ratingCheckTitle: 'Rating (1-10)',
    ratingCheck:
      'Please review the information given and give it a ranking based on its quality, use all the context you can possibly use when deciding upon the final conclusion. The rating is a scale from 1 to 10.',
    summarizeTextTitle: 'Summarise text',
    summarizeText:
      'Please review the text given and summarize for me with each key point highlighted and structured in a readable and understandable way',
    validateDriversLicenseTitle: 'Validate drivers license',
    validateDriversLicense:
      'You are reviewing a copy of a drivers license where you will validate the eligibility on the data present on the license. The data that is important is the following: General information and appearance, expiration date of the drivers license',
    validatePassportTitle: 'Validate passport',
    validatePassport:
      'You are reviewing a copy of an individuals passport where you will validate the eligibility on the data present on the given passport. The data that is important for our validation process is the following: General information and appearance, expiration date of the passport',
    validateIDTitle: 'Validate personal identification',
    validateID:
      'You are reviewing a copy of an individuals identification papers where you will validate the eligibility on the data present on the given identification. The data that is important for our validation process is the following: General information, appearance and type of identification which should be either a valid passport or valid drivers license',
    validateEducationalRecordTitle: 'Validate educational record',
    validateEducationalRecord:
      'You are reviewing a copy of an individuals educational certificate papers where you will validate the eligibility on the data present on the given certificate. The data that is important for our validation process is the following: General information and appearance, type of certificate',
    validateCriminalRecordTitle: 'Validate criminal record',
    validateCriminalRecord:
      'You are reviewing a copy of an individuals criminal record where you will validate the eligibility on the data present on the given record. The data that is important for our validation process is the following: General information and appearance, that the individual has a clean criminal record',
    otherTitle: 'Other / Custom',
    other: '',
    cvRankingTitle: 'Rank CV',
    cvRanking:
      'You are validating a CV from an applicant for a job position with the job description <ENTER YOUR JOB DESCRIPTION HERE>. Please give this CV a rating from 1-10. Please be non-biased towards race & gender.',
    cvSummarizeTitle: 'Summarize CV',
    cvSummarize:
      'You are summarizing the information in a CV document from an applicant for a job position with the job description <ENTER YOU JOB DESCRIPTION>. Please give a short summarization in bullet point format and give a short comment on the quality of the applicant towards the job position.',
    infoText: 'Ask AI is a tool that helps you analyse and format text data',
    infoPrompt: 'Create the prompt for AI to process selected field',
    infoField: 'Select field/input you want to analyse',
    infoAnalyze: 'AI Analyses the input based on your specifications and guidelines',
    infoOutputToken:
      'AI outputs a token result based on your parameters. This token can be used in various ways, e.g. as a condition for branches',
    infoOutputText:
      'AI outputs a summarised text you can supplement your data with or help you analyse and review content',
  },
};

export const getOutputValue = (templateName: TemplateName): VariableValue[] => {
  switch (templateName) {
    case 'moodCheck':
      return [{ value: 'Angry' }, { value: 'Happy' }, { value: 'Sad' }, { value: 'Neutral' }];
    case 'ratingCheck':
    case 'cvRanking':
      return [
        { value: '1' },
        { value: '2' },
        { value: '3' },
        { value: '4' },
        { value: '5' },
        { value: '6' },
        { value: '7' },
        { value: '8' },
        { value: '9' },
        { value: '10' },
      ];
    case 'validateDriversLicense':
    case 'validatePassport':
    case 'validateID':
    case 'validateEducationalRecord':
    case 'validateCriminalRecord':
      return [{ value: 'Needs review' }, { value: 'Looks good' }];
    default:
      return [];
  }
};

type InfoType = 'field' | 'askAI' | 'prompt' | 'outputToken' | 'outputText';

const TYPE_TO_VARIANT: Record<InfoType, InfoBoxVariant> = {
  field: 'green',
  askAI: 'blue',
  prompt: 'blue',
  outputToken: 'indigo',
  outputText: 'indigo',
};

const TYPE_TO_TEXT: Record<InfoType, keyof typeof locale.en> = {
  field: 'infoField',
  askAI: 'infoPrompt',
  prompt: 'infoPrompt',
  outputToken: 'infoOutputToken',
  outputText: 'infoOutputText',
};

type CloseableInfoType = 'field' | 'prompt';

const TYPE_TO_OPTION_KEY: Record<CloseableInfoType, keyof CompanyUserOptions> = {
  field: 'hasHiddenAskAIField',
  prompt: 'hasHiddenAskAIPrompt',
};

const TYPE_TO_ICON: Record<InfoType, ReactElement<IconProps>> = {
  field: <IconCheckmark />,
  askAI: <IconAskAI />,
  prompt: <IconTextLong />,
  outputToken: <IconOutput />,
  outputText: <IconOutput />,
};

export const AskAIInfoBox = ({ infoType }: { infoType: InfoType }) => {
  const { t } = useScopedTranslate(locale);
  const variant = TYPE_TO_VARIANT[infoType];
  const text = t(TYPE_TO_TEXT[infoType]);
  const icon = TYPE_TO_ICON[infoType];
  return <InfoBox variant={variant} text={text} icon={icon} />;
};

export const AskAIInfoBoxClosable = ({ infoType }: { infoType: CloseableInfoType }) => {
  const { t } = useScopedTranslate(locale);
  const optionKey = TYPE_TO_OPTION_KEY[infoType];
  const variant = TYPE_TO_VARIANT[infoType];
  const text = t(TYPE_TO_TEXT[infoType]);
  const icon = TYPE_TO_ICON[infoType];
  return <InfoBoxClosable optionKey={optionKey} variant={variant} text={text} icon={icon} />;
};
